import { useApp } from 'contexts/app';
import { useSnackbar } from 'contexts/snackbar';
import useAutoSaveForm from 'hooks/settings/useAutoSaveForm';
import { LANGUAGE_OPTIONS } from 'locales/constants';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { COURSE_DETAILS_FIELD } from 'utils/settings';

const getLanguageOption = (language) =>
    LANGUAGE_OPTIONS.find((option) => option.key === language || option.value === language) || LANGUAGE_OPTIONS[0];

const getCourseValues = (course) => ({
    name: course?.name || '',
    language: course?.language ? getLanguageOption(course.language) : null,
});

const useCourseDetailsForm = ({ onDirtyChange } = {}) => {
    const { course, updateCourse } = useApp();
    const { message } = useSnackbar();
    const { t } = useTranslation();
    const initializedCourseId = useRef(null);

    const getErrorMessage = useCallback(
        (key, value) =>
            key === COURSE_DETAILS_FIELD.NAME && !value?.trim()
                ? t('course-settings.course-details.enter-name', 'Please enter a course name')
                : '',
        [t]
    );
    const formSettings = useAutoSaveForm({
        fields: COURSE_DETAILS_FIELD,
        getErrorMessage,
        initialValues: getCourseValues(course),
        normalize: (values) => ({ ...values, name: values.name.trim() }),
        persist: async (values) => {
            const saved = await updateCourse({ name: values.name, language: values.language?.value });
            if (!saved) throw new Error('Unable to update course settings.');
        },
        onSuccess: () =>
            message.success(t('message.course-settings-updated', 'Course settings have been updated successfully.')),
        onError: () => message.error(t('message.something-went-wrong', 'Something went wrong.')),
    });
    const { isDirty, reset } = formSettings;

    useEffect(() => {
        const courseId = course?.id || course?.course_id;
        if (!courseId || initializedCourseId.current === courseId) return;
        initializedCourseId.current = courseId;
        reset(getCourseValues(course));
    }, [course, reset]);
    useEffect(() => onDirtyChange?.(isDirty), [isDirty, onDirtyChange]);

    return formSettings;
};

export default useCourseDetailsForm;
